import { useRef, useEffect, useState } from 'react';
import Editor, { OnMount, OnChange, loader } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { ValidationError } from '../../types/ontology';
import './TurtleEditor.css';

// Turtle syntax highlighting definition
const TURTLE_LANGUAGE_ID = 'turtle';

interface TurtleEditorProps {
    value: string;
    onChange: (value: string) => void;
    errors: ValidationError[];
}

// Define the theme before the editor loads
loader.init().then((monaco) => {
    // Register Turtle language
    monaco.languages.register({ id: TURTLE_LANGUAGE_ID });

    // Turtle syntax highlighting
    monaco.languages.setMonarchTokensProvider(TURTLE_LANGUAGE_ID, {
        tokenizer: {
            root: [
                // Comments
                [/#.*$/, 'comment'],

                // Prefixes
                [/@prefix\s+/, 'keyword', '@prefixDef'],
                [/@base\s+/, 'keyword'],

                // URIs
                [/<[^>]+>/, 'string.uri'],

                // Prefixed names
                [/[a-zA-Z_][\w-]*:[\w-]*/, 'type.identifier'],

                // Literals
                [/"""/, 'string', '@multilineString'],
                [/"([^"\\]|\\.)*"/, 'string'],
                [/'([^'\\]|\\.)*'/, 'string'],

                // Language tags
                [/@[a-zA-Z]+(-[a-zA-Z]+)*/, 'tag'],

                // Datatypes
                [/\^\^/, 'operator'],

                // Numbers
                [/[+-]?\d+(\.\d+)?([eE][+-]?\d+)?/, 'number'],

                // Booleans
                [/\b(true|false)\b/, 'keyword'],

                // Blank nodes
                [/_:[\w-]+/, 'variable'],
                [/\[/, 'bracket', '@blankNode'],

                // Punctuation
                [/[.,;]/, 'delimiter'],
                [/[()]/, 'bracket'],

                // RDF/OWL keywords (common classes)
                [/\b(rdf:type|a)\b/, 'keyword'],
            ],

            prefixDef: [
                [/[\w-]+:/, 'namespace'],
                [/<[^>]+>/, 'string.uri', '@pop'],
                [/\./, 'delimiter', '@pop'],
            ],

            multilineString: [
                [/[^"]+/, 'string'],
                [/"""/, 'string', '@pop'],
                [/"/, 'string'],
            ],

            blankNode: [
                { include: 'root' },
                [/\]/, 'bracket', '@pop'],
            ],
        },
    });

    // Editor theme for Fallout style
    monaco.editor.defineTheme('fallout-desert', {
        base: 'vs-dark',
        inherit: true,
        rules: [
            { token: 'comment', foreground: '6a5f4d', fontStyle: 'italic' },
            { token: 'keyword', foreground: 'ff6b2b' },
            { token: 'string', foreground: '4ade80' },
            { token: 'string.uri', foreground: '3dd6d0' },
            { token: 'number', foreground: 'ffb347' },
            { token: 'type.identifier', foreground: 'e8d5b7' },
            { token: 'namespace', foreground: 'ff6b2b' },
            { token: 'variable', foreground: 'c9b896' },
            { token: 'tag', foreground: 'ffb347' },
            { token: 'operator', foreground: '8a7a5c' },
            { token: 'delimiter', foreground: '8a7a5c' },
            { token: 'bracket', foreground: 'c9b896' },
        ],
        colors: {
            'editor.background': '#1a1410',
            'editor.foreground': '#e8d5b7',
            'editorLineNumber.foreground': '#6a5f4d',
            'editorLineNumber.activeForeground': '#ffb347',
            'editor.lineHighlightBackground': '#2d241820',
            'editor.selectionBackground': '#ff6b2b40',
            'editorCursor.foreground': '#ff6b2b',
            'editorIndentGuide.background': '#3d302050',
            'editorIndentGuide.activeBackground': '#ff6b2b50',
        },
    });
}).catch((error) => {
    console.error('Failed to initialize Monaco:', error);
});

function TurtleEditor({ value, onChange, errors }: TurtleEditorProps) {
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<typeof import('monaco-editor') | null>(null);
    const [isReady, setIsReady] = useState(false);

    const handleEditorMount: OnMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
        setIsReady(true);

        // Configure editor options
        editor.updateOptions({
            minimap: { enabled: false },
            fontSize: 14,
            fontFamily: "'Share Tech Mono', monospace",
            lineNumbers: 'on',
            renderLineHighlight: 'all',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
        });
    };

    const handleChange: OnChange = (newValue) => {
        if (newValue !== undefined) {
            onChange(newValue);
        }
    };

    // Update error markers
    useEffect(() => {
        if (!editorRef.current || !monacoRef.current || !isReady) return;

        const model = editorRef.current.getModel();
        if (!model) return;

        const markers = errors.map(error => ({
            severity: error.severity === 'error'
                ? monacoRef.current!.MarkerSeverity.Error
                : error.severity === 'warning'
                    ? monacoRef.current!.MarkerSeverity.Warning
                    : monacoRef.current!.MarkerSeverity.Info,
            startLineNumber: error.line,
            startColumn: error.column,
            endLineNumber: error.line,
            endColumn: model.getLineMaxColumn(error.line),
            message: error.message,
        }));

        monacoRef.current.editor.setModelMarkers(model, 'rdf-validation', markers);
    }, [errors, isReady]);

    return (
        <div className="turtle-editor">
            <Editor
                height="100%"
                defaultLanguage={TURTLE_LANGUAGE_ID}
                defaultValue={value}
                onChange={handleChange}
                onMount={handleEditorMount}
                theme="fallout-desert"
                loading={<div className="loading"><div className="loading__spinner" /></div>}
                options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    fontFamily: "'Share Tech Mono', monospace",
                }}
            />
        </div>
    );
}

export default TurtleEditor;
